import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getOrders, exportOrders } from '../../actions/order';
import { getSectors } from '../../actions/sector';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Table,
    Badge,
    Alert,
    Spinner,
    Toast,
    ToastContainer,
    Pagination
} from 'react-bootstrap';
import { Play, Pause, Download } from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";

/**
 * Orders Component with Enhanced URL Query Parameter Support
 *
 * This component now supports extracting all query parameters from the URL:
 * - phone: Filter by phone number
 * - paymentId: Filter by payment ID
 * - orderId: Filter by order ID
 * - startDate: Filter by start date (YYYY-MM-DD)
 * - endDate: Filter by end date (YYYY-MM-DD)
 * - latest: Sort order (true/false)
 * - page: Page number for pagination
 * - user: Filter by user id
 * - sector: Filter by sector id
 *
 * Example URLs:
 * - /orders?phone=1234567890
 * - /orders?startDate=2024-01-01&endDate=2024-01-31
 * - /orders?orderId=ORD123&latest=false&page=2
 * - /orders?user=1234567890
 * - /orders?sector=68f4b6bf825f40a3d9e11a86
 */
const Orders = ({ orders, getOrders, exportOrders, getSectors, sectors, pageList, pages, activePage, loading }) => {
    // Get location from React Router v6 hook
    const location = useLocation();

    const initialState = {
        paymentId:'',
        startDate:'',
        endDate:'',
        phone:'',
        orderId:'',
        user: '',
        sector: '',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [specificUserId, setSpecificUserId] = useState("");
    const [autoRefresh, setAutoRefresh] = useState(() => {
        // Load auto-refresh preference from localStorage
        const saved = localStorage.getItem('ordersAutoRefresh');
        return saved ? JSON.parse(saved) : false;
    });
    const [exportLoading, setExportLoading] = useState(false);

    const handleExportOrders = async () => {
        setExportLoading(true);
        try {
            await exportOrders(queryString);
        } finally {
            setExportLoading(false);
        }
    };

    const showAlert = (message, variant = 'danger') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    // Function to extract query parameters from URL
    const getQueryParams = (search) => {
        const params = new URLSearchParams(search);
        const queryParams = {};
        // Extract all query parameters
        for (const [key, value] of params.entries()) {
            queryParams[key] = value;
        }
        return queryParams;
    };

    // Function to build query string from parameters
    const buildQueryString = (params) => {
        const queryParts = [];
        // Add default parameters
        queryParts.push(`page=${params.page || 1}`);
        queryParts.push(`latest=${params.latest || true}`);
        // Add other parameters if they exist
        if (params.phone) queryParts.push(`phone=${params.phone}`);
        if (params.paymentId) queryParts.push(`paymentId=${params.paymentId}`);
        if (params.orderId) queryParts.push(`orderId=${params.orderId}`);
        if (params.startDate) queryParts.push(`startDate=${params.startDate}`);
        if (params.endDate) queryParts.push(`endDate=${params.endDate}`);
        if (params.user) queryParts.push(`user=${params.user}`);
        if (params.sector) queryParts.push(`sector=${params.sector}`);
        return queryParts.join('&');
    };

    // Function to validate query parameters
    const validateQueryParams = (params) => {
        const errors = [];
        // Validate phone number format (10 digits)
        if (params.phone && !/^\d{10}$/.test(params.phone)) {
            errors.push('Phone number must be exactly 10 digits');
        }
        // Validate date format (YYYY-MM-DD)
        if (params.startDate && !/^\d{4}-\d{2}-\d{2}$/.test(params.startDate)) {
            errors.push('Start date must be in YYYY-MM-DD format');
        }
        if (params.endDate && !/^\d{4}-\d{2}-\d{2}$/.test(params.endDate)) {
            errors.push('End date must be in YYYY-MM-DD format');
        }
        // Validate date range
        if (params.startDate && params.endDate) {
            const start = new Date(params.startDate);
            const end = new Date(params.endDate);
            if (end < start) {
                errors.push('End date must be after start date');
            }
        }
        return errors;
    };

    useEffect(() => {
        getSectors();
    }, [getSectors]);

    useEffect(() => {
        document.title = 'Orders';
        if(orders === undefined){
            let query = queryString;
            if(location && location.search){
                // Extract all query parameters from URL
                const urlParams = getQueryParams(location.search);
                // Validate URL parameters
                const validationErrors = validateQueryParams(urlParams);
                if (validationErrors.length > 0) {
                    showAlert(`Invalid URL parameters: ${validationErrors.join(', ')}`, 'warning');
                }
                // Update form data with URL parameters
                const updatedFormData = {
                    ...formData,
                    phone: urlParams.phone || '',
                    paymentId: urlParams.paymentId || '',
                    orderId: urlParams.orderId || '',
                    startDate: urlParams.startDate || '',
                    endDate: urlParams.endDate || '',
                    latest: urlParams.latest !== undefined ? urlParams.latest === 'true' : true,
                    user: urlParams.user || '',
                    sector: urlParams.sector || ''
                };
                setFormData(updatedFormData);
                // Set specific user ID if phone parameter exists
                if (urlParams.phone) {
                    setSpecificUserId(urlParams.phone);
                }
                // Build query string from URL parameters
                query = buildQueryString(urlParams);
                setQueryString(query);
            }
            getOrders(query);
        } else {
            processOrderData(orders);
            setCurrentPage(activePage || 1);
        }
    }, [location, orders, getOrders]);

    // Auto-refresh effect
    useEffect(() => {
        let interval = null;
        if (autoRefresh) {
            interval = setInterval(() => {
                getOrders(queryString);
            }, 30000); // 30 seconds
        }

        // Cleanup function
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [autoRefresh, getOrders, queryString]);

    const processOrderData = (orderData) => {
        const processedData = orderData.map(order => ({
            orderId: order.orderId,
            orderStatus: order.orderStatus,
            _id: order._id,
            gstAmount: order.gstAmount || 0,
            amount: order.amount || 0,
            mrp: order.mrp || 0,
            userName: order.user ? order.user.name : "Deleted User",
            userId: order.user ? order.user._id : "deleted",
            userPhone: order.user ? order.user.phone : "N/A",
            userType: order.userType || 'standard',
            sectorId: order.sector ? order.sector._id : null,
            sectorName: order.sector ? order.sector.name : 'N/A',
            time: new Date(order.date).toLocaleString(),
            rawTime: new Date(order.date)
        }));

        // Sort by latest if needed
        if (formData.latest) {
            processedData.sort((a, b) => b.rawTime - a.rawTime);
        } else {
            processedData.sort((a, b) => a.rawTime - b.rawTime);
        }

        setData(processedData);
    };




    const {
        paymentId,
        startDate,
        endDate,
        phone,
        orderId,
        user,
        sector,
        latest
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        
        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate).getTime();
            const endDate = new Date(formData.endDate).getTime();
            if(endDate < startDate){
                showAlert("End date must be after start date");
                return;
            }
        }
        
        // Build query parameters object
        const queryParams = {
            page: 1,
            latest: formData.latest,
            phone: formData.phone || undefined,
            paymentId: formData.paymentId || undefined,
            orderId: formData.orderId || undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            user: formData.user || undefined,
            sector: formData.sector || undefined
        };
        // Build query string from parameters
        const newQueryString = buildQueryString(queryParams);
        setQueryString(newQueryString);
        setCurrentPage(1);
        getOrders(newQueryString);
    };

    const handleSort = (field) => {
        const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(direction);
        
        const sortedData = [...data].sort((a, b) => {
            if (direction === 'asc') {
                return a[field] > b[field] ? 1 : -1;
            }
            return a[field] < b[field] ? 1 : -1;
        });
        setData(sortedData);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1"></i>
            : <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const clearFilters = () => {
        setFormData(initialState);
        setSpecificUserId("");
        const newQueryString = buildQueryString({ page: 1, latest: true });
        setQueryString(newQueryString);
        setCurrentPage(1);
        getOrders(newQueryString);
        showAlert("Filters cleared successfully!", "info");
    };

    const toggleAutoRefresh = () => {
        const newAutoRefresh = !autoRefresh;
        setAutoRefresh(newAutoRefresh);
        // Save preference to localStorage
        localStorage.setItem('ordersAutoRefresh', JSON.stringify(newAutoRefresh));
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pages) return;
        
        const newQueryString = queryString.replace(`page=${currentPage}`, `page=${page}`);
        setQueryString(newQueryString);
        setCurrentPage(page);
        getOrders(newQueryString);
    };

    const renderPagination = () => {
        if (!pages || pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pages) {
            if (endPage < pages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={pages} onClick={() => handlePageChange(pages)}>
                    {pages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={currentPage === pages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    const getStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'captured':
            case 'payment successful':
                return <Badge bg="success">Captured</Badge>;
            case 'processing':
                return <Badge bg="warning">Processing</Badge>;
            case 'failed':
                return <Badge bg="danger">Failed</Badge>;
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>;
            default:
                return <Badge bg="info">{status || 'Unknown'}</Badge>;
        }
    };

    const getUserTypeBadge = (userType) => {
        switch(userType?.toLowerCase()) {
            case 'member':
                return <Badge bg="primary">Member</Badge>;
            case 'standard':
                return <Badge bg="secondary">Standard</Badge>;
            default:
                return <Badge bg="light" text="dark">{userType || 'Standard'}</Badge>;
        }
    };

    const renderOrderTable = () => {
        if (!data || data.length === 0) {
            return (
                <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No order data found. Try adjusting your filters.
                </Alert>
            );
        }

        return (
            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('orderId')}
                            >
                                Order ID
                                {getSortIcon('orderId')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('userName')}
                            >
                                User
                                {getSortIcon('userName')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('userPhone')}
                            >
                                Phone
                                {getSortIcon('userPhone')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('sectorName')}
                            >
                                Sector
                                {getSortIcon('sectorName')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('userType')}
                            >
                                User Type
                                {getSortIcon('userType')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('amount')}
                            >
                                Amount (₹)
                                {getSortIcon('amount')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('gstAmount')}
                            >
                                GST (₹)
                                {getSortIcon('gstAmount')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('mrp')}
                            >
                                MRP (₹)
                                {getSortIcon('mrp')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('orderStatus')}
                            >
                                Status
                                {getSortIcon('orderStatus')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('time')}
                            >
                                Date & Time
                                {getSortIcon('time')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={row._id || index}>
                                <td>
                                    <Link 
                                        to={`/inventory/v1.2/orders/${row._id}`}
                                        className="text-decoration-none fw-bold text-primary order-link"
                                        title={`View order details: ${row.orderId}`}
                                    >
                                        {row.orderId}
                                    </Link>
                                </td>
                                <td>
                                    <div>
                                        {row.userId !== 'deleted' ? (
                                            <Link 
                                                to={`/inventory/v1.2/users/${row.userId}`}
                                                className="text-decoration-none fw-bold text-primary user-link"
                                                title={`View user details: ${row.userName}`}
                                            >
                                                {row.userName}
                                            </Link>
                                        ) : (
                                            <span className="text-muted fst-italic">
                                                {row.userName}
                                            </span>
                                        )}
                                        <div className="mt-1">
                                            {getUserTypeBadge(row.userType)}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className={`${row.userPhone === 'N/A' ? 'text-muted fst-italic' : ''}`}>
                                        {row.userPhone}
                                    </span>
                                </td>
                                <td>
                                    {row.sectorId ? (
                                        <Link 
                                            to={`/inventory/v1.2/sectors/${row.sectorId}`}
                                            className="text-decoration-none fw-bold text-primary"
                                            title={`View sector details: ${row.sectorName}`}
                                        >
                                            {row.sectorName}
                                        </Link>
                                    ) : (
                                        <span className="text-muted fst-italic">
                                            {row.sectorName}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {getUserTypeBadge(row.userType)}
                                </td>
                                <td>
                                    <span className="fw-bold text-success">
                                        ₹{row.amount.toLocaleString()}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-info">
                                        ₹{row.gstAmount.toLocaleString()}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-secondary">
                                        ₹{row.mrp.toLocaleString()}
                                    </span>
                                </td>
                                <td>
                                    {getStatusBadge(row.orderStatus)}
                                </td>
                                <td>
                                    <small className="text-muted">
                                        {new Date(row.rawTime).toLocaleDateString()}<br/>
                                        {new Date(row.rawTime).toLocaleTimeString()}
                                    </small>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-primary mb-0">
                    <i className="fas fa-shopping-cart me-2"></i>
                    Orders Management
                </h2>
                <div className="d-flex gap-2 align-items-center">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleExportOrders}
                        disabled={exportLoading}
                        className="d-flex align-items-center"
                    >
                        {exportLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download size={16} className="me-1" />
                                Export CSV
                            </>
                        )}
                    </Button>
                    <Button
                        variant={autoRefresh ? "success" : "outline-secondary"}
                        size="sm"
                        onClick={toggleAutoRefresh}
                        className="d-flex align-items-center"
                    >
                        {autoRefresh ? (
                            <>
                                <Pause size={16} className="me-1" />
                                Auto Refresh ON
                            </>
                        ) : (
                            <>
                                <Play size={16} className="me-1" />
                                Auto Refresh OFF
                            </>
                        )}
                    </Button>
                    {specificUserId && (
                        <Badge bg="info">
                            <i className="fas fa-user me-1"></i>
                            Phone Specific View
                        </Badge>
                    )}
                </div>
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-receipt me-2"></i>
                                        Order ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="orderId"
                                        value={orderId}
                                        onChange={onChange}
                                        placeholder="Enter order ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-phone me-2"></i>
                                        Phone Number
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={phone}
                                        onChange={onChange}
                                        placeholder="Enter phone number"
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-user me-2"></i>
                                        User ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="user"
                                        value={user}
                                        onChange={onChange}
                                        placeholder="Enter user ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-credit-card me-2"></i>
                                        Payment ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="paymentId"
                                        value={paymentId}
                                        onChange={onChange}
                                        placeholder="Enter payment ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={latest}
                                        onChange={onChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-map-marker-alt me-2"></i>
                                        Sector
                                    </Form.Label>
                                    <Form.Select
                                        name="sector"
                                        value={sector}
                                        onChange={onChange}
                                    >
                                        <option value="">All Sectors</option>
                                        {sectors && sectors.map(sec => (
                                            <option key={sec._id} value={sec._id}>
                                                {sec.name} {sec.pincode ? `(${sec.pincode})` : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2 w-100">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex-fill"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-search me-2"></i>
                                                Apply Filters
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Summary Cards */}
            {data.length > 0 && (
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-shopping-cart text-primary fa-2x mb-2"></i>
                                <h5 className="card-title">Total Orders</h5>
                                <h3 className="text-primary">{data.length}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                                <h5 className="card-title">Captured</h5>
                                <h3 className="text-success">
                                    {data.filter(row => row.orderStatus?.toLowerCase() === 'captured' || row.orderStatus?.toLowerCase() === 'payment successful').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-clock text-info fa-2x mb-2"></i>
                                <h5 className="card-title">Created</h5>
                                <h3 className="text-info">
                                    {data.filter(row => row.orderStatus?.toLowerCase() === 'created').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-times-circle text-danger fa-2x mb-2"></i>
                                <h5 className="card-title">Failed</h5>
                                <h3 className="text-danger">
                                    {data.filter(row => row.orderStatus?.toLowerCase() === 'failed').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Results Card */}
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-table me-2"></i>
                        Order Records
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        {pages > 1 && (
                            <small className="text-light">
                                Page {currentPage} of {pages}
                            </small>
                        )}
                        {data.length > 0 && (
                            <Badge bg="light" text="dark">
                                {data.length} record{data.length !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading order data...</div>
                        </div>
                    ) : (
                        <>
                            {renderOrderTable()}
                            {/* Pagination */}
                            {pages > 1 && (
                                <div className="p-3 border-top bg-light">
                                    {renderPagination()}
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : toastVariant === 'info' ? 'Info' : 'Alert'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? '' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <style>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .user-select-none {
                    user-select: none;
                }
                .table th:hover {
                    background-color: #495057 !important;
                }
                .user-link:hover, .order-link:hover {
                    text-decoration: underline !important;
                    color: #0d6efd !important;
                }
            `}</style>
        </Container>
    )
}

Orders.propTypes = {
    getOrders: PropTypes.func.isRequired,
    exportOrders: PropTypes.func.isRequired,
    getSectors: PropTypes.func.isRequired,
    orders: PropTypes.array,
    sectors: PropTypes.array,
    pages: PropTypes.number,
    activePage: PropTypes.number,
    pageList: PropTypes.array,
    loading: PropTypes.bool
} 
const mapStateToProps = state => ({
    orders: state.order.orders,
    sectors: state.sector.sectors,
    pages: state.order.pages,
    pageList : state.order.pageList,
    activePage : state.order.activePage,
    loading: state.order.loading
})

export default connect( mapStateToProps, { getOrders, exportOrders, getSectors } )( Orders );